"use client"

import { X } from "lucide-react"
import { useRef, useState } from "react"
import Button from "../shared/Button"
import avatarIcon from "../../assets/icons/avatar.svg"

export default function UploadPhotoModal({ isOpen, onClose, onImageSelect }) {
  const fileInputRef = useRef(null)
  const [selectedImage, setSelectedImage] = useState(null)
  const [previewUrl, setPreviewUrl] = useState(null)
  const [isLoading, setIsLoading] = useState(false)

  if (!isOpen) return null

  // const handleBackdropClick = (e) => {
  //   if (e.target === e.currentTarget) {
  //     onClose()
  //   }
  // }

  const handleFileSelect = (event) => {
    const file = event.target.files[0]
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp']
      if (!allowedTypes.includes(file.type)) {
        alert('Please select a valid image file (JPEG, PNG, or WebP)')
        return
      }

      // Validate file size (1MB = 1024 * 1024 bytes)
      if (file.size > 1024 * 1024) {
        alert('Image size must be less than 1MB')
        return
      }

      setSelectedImage(file)
      
      // Create preview URL
      const reader = new FileReader()
      reader.onload = (e) => {
        setPreviewUrl(e.target.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleUploadFromDevice = () => {
    fileInputRef.current?.click()
  }


  const handleConfirmUpload = async () => {
    if (selectedImage && onImageSelect) {
      setIsLoading(true)
      try {
        await onImageSelect(selectedImage)
        onClose()
      } catch (error) {
        console.error('Error uploading image:', error)
      } finally {
        setIsLoading(false)
      }
    }
  }

  const handleClose = () => {
    setSelectedImage(null)
    setPreviewUrl(null)
    onClose()
  }

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      // onClick={handleBackdropClick}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70" />

      {/* Modal container */}
      <div className="relative z-10 flex flex-col w-full max-w-[490px] px-4 sm:px-0 pt-[30px] sm:pt-[40px] pb-[20px] sm:pb-[30px] items-center gap-[20px] sm:gap-[30px] shrink-0 rounded-[22px] bg-white shadow-2xl">
        
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 sm:top-4 sm:right-4 w-5 h-5 bg-dark-blue/40 hover:bg-dark-blue/80 rounded-full flex items-center justify-center transition-colors"
        >
          <X className="w-3 h-3 text-white" />
        </button>

        {/* Profile image area */}
        <div className="flex w-[100px] h-[100px] sm:w-[140px] sm:h-[140px] p-[15px] sm:p-[22.4px] justify-center items-center rounded-full border border-lightGrey overflow-hidden">
          <img
            src={previewUrl || avatarIcon}
            alt="profile"
            className="w-full h-full rounded-full object-cover"
          />
        </div>

        {/* Text Section */}
        <div className="flex flex-col items-center gap-2 px-2 sm:px-6 text-center">
          <h2 className="text-lg sm:text-[25px] font-bold text-dark-blue">
            {selectedImage ? 'Confirm your photo' : 'Upload a photo of you'}
          </h2>
          <p className="text-sm sm:text-[20px] text-gray font-normal">
            {selectedImage 
              ? 'This photo will be used as your profile picture.' 
              : 'Please make sure your photo clearly shows your face. Upload square 1:1 photo.'
            }
          </p>
        </div>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={handleFileSelect}
          className="hidden"
        />

        {/* Buttons */}
        <div className="flex flex-col gap-3 sm:gap-4 w-full px-2 sm:px-6">
          {!selectedImage ? (
            <>
              <Button
                onClick={handleUploadFromDevice}
                variant="outline-gradient-text"
                className="h-[45px] sm:h-[48px] !py-2.5 rounded-[10px] md:text-[18px] text-[16px]"
              >
                Choose from device
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={handleConfirmUpload}
                disabled={isLoading}
                className="h-[45px] sm:h-[48px] !py-2.5 rounded-[10px] md:text-[18px] text-[16px] disabled:opacity-50"
              >
                {isLoading ? 'Uploading...' : 'Confirm Upload'}
              </Button>

              <Button
                onClick={handleUploadFromDevice}
                variant="outline-gradient-text"
                className="h-[45px] sm:h-[48px] !py-2.5 rounded-[10px] md:text-[18px] text-[16px]"
              >
                Choose Different Photo
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
